import { useMemo } from 'react';

import { useConceptRenditions } from '@/hooks/useConceptRenditions';
import type { AspectRatioToken, VariantSetDocument } from '@/lib/concept-workbench/types';
import type { RenditionPatch, SetPatch } from '@/services/data/ConceptRenditionService';

/**
 * THE ONE-FILE SEAM between the workbench surface and persistence.
 *
 * The workbench was built against the in-memory variant-set store while a
 * parallel lane built `concept_renditions` / `concept_rendition_sets`. Every
 * component above this file talks to {@link VariantSetPersistence} and nothing
 * else, so swapping the backing store is an edit to this file alone — which is
 * exactly what happened: the real hook landed and the surface did not change.
 *
 * It stays a seam rather than collapsing into a direct `useConceptRenditions`
 * call because the workbench also needs to run with NO persistence at all (the
 * style-guide composition, and the tests for the wiring logic), and a surface
 * that can only mount against a live account is a surface nobody can iterate on.
 */
export interface VariantSetPersistence {
  document: VariantSetDocument | null;
  isLoading: boolean;
  isError: boolean;
  error: unknown;

  saveSet: (patch: SetPatch) => void;
  saveVariant: (renditionId: string, patch: RenditionPatch) => void;
  flush: () => Promise<void>;
  hasUnsavedChanges: boolean;

  ensureRenditions: (ratios?: readonly AspectRatioToken[]) => Promise<unknown>;
  addRatio: (aspectRatio: AspectRatioToken) => Promise<unknown>;
  removeRatio: (renditionId: string) => Promise<unknown>;
  /**
   * `generationToken` is passed ONLY on the dispatch call (`isGenerating:
   * true`) — it is what lets the server's own completion write
   * (`attachGeneratedMediaByCorrelation`) find this exact row without
   * depending on the client still being alive when the provider finishes.
   * See `useWorkbenchPrompt.submit`.
   */
  setGenerating: (
    renditionId: string,
    isGenerating: boolean,
    generationToken?: string,
  ) => Promise<unknown>;
  attachMedia: (renditionId: string, mediaId: string) => Promise<unknown>;
  /**
   * Turn any rendition still `is_generating` past the stale timeout into an
   * honest failed state. Called on mount and on a poll while anything in the
   * document is generating — see `useConceptWorkbench`.
   */
  reconcileStaleGenerations: () => Promise<string[]>;
  /**
   * Renditions the reconciler cleared this session because their generation
   * never finished. The canvas names that instead of showing a bare empty
   * state, so a person can tell "never tried" from "tried and did not land".
   */
  timedOutRenditionIds: ReadonlySet<string>;

  conflict: Error | null;
  reload: () => Promise<void>;
}

/** The live adapter: `concept_renditions` through the parallel lane's hook. */
export function useVariantSetPersistence(
  conceptId: string | undefined,
  platforms: readonly (string | null | undefined)[],
): VariantSetPersistence {
  const renditions = useConceptRenditions(conceptId, { platforms });

  return useMemo<VariantSetPersistence>(
    () => ({
      document: renditions.document,
      isLoading: renditions.isLoading,
      isError: renditions.isError,
      error: renditions.error,

      saveSet: renditions.saveSet,
      saveVariant: renditions.saveVariant,
      flush: () => renditions.flushAutosave(),
      hasUnsavedChanges: renditions.hasUnsavedChanges,

      ensureRenditions: (ratios) => renditions.ensureRenditions.mutateAsync({ ratios }),
      addRatio: (aspectRatio) => renditions.addRatio.mutateAsync({ aspectRatio }),
      removeRatio: (renditionId) => renditions.removeRatio.mutateAsync(renditionId),
      setGenerating: (renditionId, isGenerating, generationToken) =>
        renditions.setGenerating.mutateAsync({ renditionId, isGenerating, generationToken }),
      attachMedia: (renditionId, mediaId) =>
        renditions.attachMedia.mutateAsync({ renditionId, mediaId }),
      reconcileStaleGenerations: () => renditions.reconcileStaleGenerations(),
      timedOutRenditionIds: renditions.timedOutRenditionIds,

      conflict: renditions.conflict,
      reload: renditions.reload,
    }),
    [renditions],
  );
}

/**
 * The null adapter. Holds a document in memory and drops every write.
 *
 * Not a test double — it is how the surface mounts when the account has no
 * rendition rows yet and the user has not asked to create any. Silently failing
 * writes would be the wrong shape for that; dropping them explicitly is honest,
 * and `hasUnsavedChanges` stays false so the header never claims a save it did
 * not make.
 */
export function inMemoryPersistence(document: VariantSetDocument | null): VariantSetPersistence {
  return {
    document,
    isLoading: false,
    isError: false,
    error: null,
    saveSet: () => {},
    saveVariant: () => {},
    flush: async () => {},
    hasUnsavedChanges: false,
    ensureRenditions: async () => undefined,
    addRatio: async () => undefined,
    removeRatio: async () => undefined,
    setGenerating: async () => undefined,
    attachMedia: async () => undefined,
    reconcileStaleGenerations: async () => [],
    timedOutRenditionIds: new Set<string>(),
    conflict: null,
    reload: async () => {},
  };
}
